// Server state. One place for query keys so live updates know what to refresh.
import type {
  AgentView,
  InboxCounts,
  InboxFilter,
  MeView,
  RoutingView,
  SessionRef,
  ThreadDetail,
  ThreadView,
  TokenView,
} from "@nerves/shared";
import { type QueryClient, queryOptions, useQuery } from "@tanstack/react-query";
import { ApiError, api } from "./api";

export const keys = {
  me: ["me"] as const,
  authConfig: ["auth-config"] as const,
  threads: (filter: InboxFilter, product: string | null) => ["threads", filter, product] as const,
  threadsAll: ["threads"] as const,
  thread: (ref: string) => ["thread", ref.toUpperCase()] as const,
  threadAll: ["thread"] as const,
  tokens: ["tokens"] as const,
  sessions: ["sessions"] as const,
  mcp: ["mcp"] as const,
  agents: ["agents"] as const,
  routing: ["routing"] as const,
  admin: ["admin"] as const,
};

export interface AuthConfig {
  oidc: { providerId: string; name: string } | null;
  devLogin: boolean;
  discordLink: boolean;
}

export const authConfigQuery = queryOptions({
  queryKey: keys.authConfig,
  queryFn: () => api.get<AuthConfig>("/api/v1/auth-config"),
  staleTime: Number.POSITIVE_INFINITY,
});

export const meQuery = queryOptions({
  queryKey: keys.me,
  queryFn: async () => {
    try {
      return await api.get<MeView>("/api/v1/me");
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthorized) return null;
      throw err;
    }
  },
  staleTime: 30_000,
});

/** Cached while fresh; after sign-in / sign-out (invalidated) it asks the server again. */
export function loadMe(qc: QueryClient) {
  return qc.fetchQuery(meQuery);
}

export function useMe() {
  return useQuery(meQuery);
}

export interface ThreadPage {
  threads: ThreadView[];
  counts: InboxCounts;
}

export function threadsQuery(filter: InboxFilter, product: string | null) {
  return queryOptions({
    queryKey: keys.threads(filter, product),
    queryFn: ({ signal }) => {
      const q = new URLSearchParams({ filter });
      if (product) q.set("product", product);
      return api.get<ThreadPage>(`/api/v1/threads?${q}`, signal);
    },
  });
}

export function threadQuery(ref: string) {
  return queryOptions({
    queryKey: keys.thread(ref),
    queryFn: ({ signal }) => api.get<ThreadDetail>(`/api/v1/threads/${encodeURIComponent(ref)}`, signal),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });
}

export const tokensQuery = queryOptions({
  queryKey: keys.tokens,
  queryFn: () => api.get<TokenView[]>("/api/v1/tokens"),
});
export const sessionsQuery = queryOptions({
  queryKey: keys.sessions,
  queryFn: () => api.get<Array<SessionRef & { agentName: string }>>("/api/v1/agents/sessions"),
});
export const mcpQuery = queryOptions({
  queryKey: keys.mcp,
  queryFn: () =>
    api.get<{ url: string; serverName: string; installCommand: string; tools: string[] }>("/api/v1/agents/mcp"),
  staleTime: Number.POSITIVE_INFINITY,
});
export const agentsQuery = queryOptions({
  queryKey: keys.agents,
  queryFn: () => api.get<AgentView[]>("/api/v1/agents"),
});
export const routingQuery = queryOptions({
  queryKey: keys.routing,
  queryFn: () => api.get<RoutingView>("/api/v1/routing"),
});

export interface AdminStatus {
  discord: { configured: boolean; connected: boolean; inviteUrl: string | null };
  plannello: { configured: boolean; syncedAt: string | null; error: string | null };
  deliveries: { pending: number; failed: number; lastError: string | null };
  mcpSessions: number;
  people: Array<{
    id: string;
    name: string;
    email: string;
    isAdmin: boolean;
    discordLinked: boolean;
    lastAgentActivity: string | null;
  }>;
}

export const adminQuery = queryOptions({
  queryKey: keys.admin,
  queryFn: () => api.get<AdminStatus>("/api/v1/admin/status"),
  refetchInterval: 15_000,
});
